import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Cpu,
  Layers,
  Server,
  Database,
  ShieldCheck,
  Lock,
  FileCheck,
  Cloud,
  Network,
  Building2,
  Link2,
  KeyRound,
  Video,
  LucideIcon,
} from "lucide-react";
import { useNavigationStore } from "@/stores/navigation";
import DownloadForm from "@/components/common/DownloadForm";

interface SectionProps {
  id: string;
  children: React.ReactNode;
  className?: string;
}

const Section: React.FC<SectionProps> = ({ id, children, className = "" }) => (
  <section id={id} className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </section>
);

interface StatBulletProps {
  children: React.ReactNode;
}

const STAT_BULLET: React.FC<StatBulletProps> = ({ children }) => (
  <li className="flex items-start gap-2 text-[15px] sm:text-base text-[#1F2B59]/90">
    <span className="mt-1 inline-block size-1.5 rounded-full bg-[#7AC943]" />
    <span>{children}</span>
  </li>
);

interface CardProps {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
  accent?: string;
}

const Card: React.FC<CardProps> = ({ icon: Icon, title, children, accent = "#395091" }) => (
  <div className="group relative overflow-hidden rounded-3xl border border-[#1F2B59]/10 bg-white/80 p-5 shadow-sm transition hover:shadow-md">
    <div className="flex items-start gap-4">
      <div className="shrink-0 flex size-14 items-center justify-center rounded-2xl" style={{ backgroundColor: "#EEF2FF" }}>
        <Icon className="h-7 w-7" style={{ color: accent }} />
      </div>
      <div>
        <h3 className="text-lg font-bold text-slate-900">{title}</h3>
        <div className="mt-2 text-sm text-[#31407A]">{children}</div>
      </div>
    </div>
  </div>
);

const Technical: React.FC = () => {
  const { t } = useTranslation();
  const navigationStore = useNavigationStore();
  
  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false);

  const handleBookDemo = () => {
    navigationStore.openDemoModal();
  };

  const handleDownloadTechnicalOverview = () => {
    setDownloadFormOpen(true);
  };

  return (
    <div className="min-h-screen w-full bg-white text-slate-800">
      {/* HERO */}
      <header className="w-full text-white" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="mb-6 sm:mb-8">
            <h2
              className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight"
              style={{ fontFamily: "Montserrat, Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif" }}
            >
              <span>Eceptionist</span>
              <span className="font-bold text-[#7AC943]">CX</span>
              <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">™</sup>
            </h2>
          </div>

          {/* Page Title */}
          <p className="tracking-wide text-base sm:text-lg font-semibold text-[#7AC943] mb-2">
            {t('navigation.technicalOverview')}
          </p>

          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              {t('technicalOverview.hero.title')}
            </h1>
            <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
              {t('technicalOverview.hero.subtitle')}
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button 
                onClick={handleBookDemo}
                className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
              >
                {t('common.bookDemo')}
              </button>
              <a href="#architecture" className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition">
                {t('technicalOverview.hero.exploreTech')}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO SUMMARY */}
      <Section id="summary" className="py-8 sm:py-10">
        <div className="rounded-3xl bg-white/60 p-6 shadow-[0_1px_0_rgba(31,43,89,0.06),0_8px_24px_rgba(31,43,89,0.06)]">
          <p className="text-[15px] text-[#1F2B59]/90 sm:text-base">
            {t('technicalOverview.summary.description')}
          </p>
        </div>
      </Section>

      {/* ARCHITECTURE */}
      <Section id="architecture" className="py-8 sm:py-10 bg-gradient-to-br from-[#F9FAFB] to-[#F0F4FA]">
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t('technicalOverview.architecture.title')}</h2>
        <p className="mt-2 max-w-3xl text-sm text-[#31407A] sm:text-base">{t('technicalOverview.architecture.subtitle')}</p>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
          <Card icon={Layers} title={t('technicalOverview.architecture.presentationTier.title')}>
            {t('technicalOverview.architecture.presentationTier.description')}
          </Card>
          <Card icon={Network} title={t('technicalOverview.architecture.interfacesTier.title')}>
            {t('technicalOverview.architecture.interfacesTier.description')}
          </Card>
          <Card icon={Server} title={t('technicalOverview.architecture.serviceTier.title')}>
            {t('technicalOverview.architecture.serviceTier.description')}
          </Card>
          <Card icon={Cpu} title={t('technicalOverview.architecture.businessLogicTier.title')}>
            {t('technicalOverview.architecture.businessLogicTier.description')}
          </Card>
          <Card icon={Database} title={t('technicalOverview.architecture.dataAccessTier.title')}>
            {t('technicalOverview.architecture.dataAccessTier.description')}
          </Card>
          <Card icon={Cloud} title={t('technicalOverview.architecture.dataStorageTier.title')}>
            {t('technicalOverview.architecture.dataStorageTier.description')}
          </Card>
        </div>

        <div className="mt-8 rounded-2xl border border-[#1F2B59]/10 bg-white p-5">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">{t('technicalOverview.architecture.whyItMatters.title')}</h3>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <STAT_BULLET>{t('technicalOverview.architecture.whyItMatters.healthcareSpecific')}</STAT_BULLET>
            <STAT_BULLET>{t('technicalOverview.architecture.whyItMatters.configurableWorkflows')}</STAT_BULLET>
            <STAT_BULLET>{t('technicalOverview.architecture.whyItMatters.scalesSeamlessly')}</STAT_BULLET>
            <STAT_BULLET>{t('technicalOverview.architecture.whyItMatters.mobileFirst')}</STAT_BULLET>
          </ul>
        </div>
      </Section>

      {/* INTEGRATION */}
      <Section id="integration" className="py-8 sm:py-10">
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t('technicalOverview.integration.title')}</h2>
        <p className="mt-2 max-w-3xl text-sm text-[#31407A] sm:text-base">{t('technicalOverview.integration.subtitle')}</p>

        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3">
          <Card icon={Link2} title={t('technicalOverview.integration.standardsApis.title')}>
            {t('technicalOverview.integration.standardsApis.description')}
          </Card>
          <Card icon={KeyRound} title={t('technicalOverview.integration.identitySso.title')}>
            {t('technicalOverview.integration.identitySso.description')}
          </Card>
          <Card icon={Building2} title={t('technicalOverview.integration.ehrConnectivity.title')}>
            {t('technicalOverview.integration.ehrConnectivity.description')}
          </Card>
          <Card icon={Video} title={t('technicalOverview.integration.telehealthCommunications.title')}>
            {t('technicalOverview.integration.telehealthCommunications.description')}
          </Card>
          <Card icon={Network} title={t('technicalOverview.integration.enterpriseTools.title')}>
            {t('technicalOverview.integration.enterpriseTools.description')}
          </Card>
          <Card icon={Server} title={t('technicalOverview.integration.deploymentOptions.title')}>
            {t('technicalOverview.integration.deploymentOptions.description')}
          </Card>
        </div>
      </Section>

      {/* SECURITY & COMPLIANCE */}
      <Section id="security" className="py-8 sm:py-10 bg-gradient-to-br from-[#F9FAFB] to-[#F0F4FA]">
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{t('technicalOverview.security.title')}</h2>
        <p className="mt-2 max-w-3xl text-sm text-[#31407A] sm:text-base">{t('technicalOverview.security.subtitle')}</p>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <div className="flex flex-col items-center gap-2"><FileCheck className="w-8 h-8 text-[#395091]" /><span className="text-slate-700 font-medium">SOC 2 Type II</span></div>
          <div className="flex flex-col items-center gap-2"><FileCheck className="w-8 h-8 text-[#395091]" /><span className="text-slate-700 font-medium">SOC 3</span></div>
          <div className="flex flex-col items-center gap-2"><ShieldCheck className="w-8 h-8 text-[#395091]" /><span className="text-slate-700 font-medium">HIPAA / PHIPA</span></div>
          <div className="flex flex-col items-center gap-2"><Lock className="w-8 h-8 text-[#395091]" /><span className="text-slate-700 font-medium">GDPR-Aligned / DPF</span></div>
        </div>

        <div className="mt-8 rounded-2xl border border-[#1F2B59]/10 bg-white p-5">
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">{t('technicalOverview.security.operationalSafeguards.title')}</h3>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <STAT_BULLET>{t('technicalOverview.security.operationalSafeguards.penetrationTesting')}</STAT_BULLET>
            <STAT_BULLET>{t('technicalOverview.security.operationalSafeguards.monitoring')}</STAT_BULLET>
            <STAT_BULLET>{t('technicalOverview.security.operationalSafeguards.roleBasedAccess')}</STAT_BULLET>
            <STAT_BULLET>{t('technicalOverview.security.operationalSafeguards.segregatedEnvironments')}</STAT_BULLET>
          </ul>
          <p className="mt-4 text-[15px] text-[#1F2B59]/90 sm:text-base">{t('technicalOverview.security.operationalSafeguards.description')}</p>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section id="contact" className="relative mt-2 sm:mt-4">
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">{t('technicalOverview.cta.title')}</h3>
              <p className="opacity-90 text-base sm:text-lg">{t('technicalOverview.cta.subtitle')}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
              <button 
                onClick={handleBookDemo}
                className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
              >
                {t('technicalOverview.cta.requestDemo')}
              </button>
              <button 
                onClick={handleDownloadTechnicalOverview}
                className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
              >
                {t('technicalOverview.cta.downloadTechnicalOverview')}
              </button>
            </div>
        </div>
      </div>
      </Section>

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="EceptionistCX Technical Overview"
        pdfPath="/PDF/EceptionistCX_Technical_Overview.pdf"
      />
    </div>
  );
};

export default Technical;


